import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { t } from '../../../i18n/uiText';

import { telegramService } from '../../../services/telegram';

import { useTelegramOverlayBack } from '../../../hooks/useTelegramOverlayBack';

import { TG_OVERLAY_Z } from '../../../services/telegramBackStack';

import { useVerseInsightChat } from '../../../hooks/useVerseInsightChat';

import {

  formatAssistantInsightHtml,

  wrapInsightProseHtml,

} from '../../../utils/assistantInsightHtml';

import {

  getInsightSurfaceAssistant,

  getQuestionThreadMessages,

} from '../../../utils/verseInsightHelpers';

import BfSheet from './BfSheet';

import { BfIconClose } from './BfIcons';
import { ChatStreamMode } from '../../../constants/chatStreamMode';



const VERSE_SNIPPET_MAX = 96;

const INSIGHT_TABS = ['explain', 'simple', 'application'];



function isTgMobileFullscreen() {

  return telegramService.isTelegramWebApp && !telegramService.isTelegramWideLayout;

}



function resolveInitialTab(tab) {

  if (tab === 'question') return 'explain';

  return INSIGHT_TABS.includes(tab) ? tab : 'explain';

}



function clipVerseSnippet(text, maxLen = VERSE_SNIPPET_MAX) {

  const raw = (text ?? '').replace(/\s+/g, ' ').trim();

  if (!raw) return '';

  if (raw.length <= maxLen) return raw;

  return `${raw.slice(0, maxLen - 1).trim()}…`;

}



function tabLabel(tab) {

  switch (tab) {

    case 'explain':

      return t('chatActionLabel');

    case 'simple':

      return t('chatFollowUpSimple');

    case 'application':

      return t('application');

    default:

      return tab;

  }

}



function formatInsightHtml(text, translation) {

  return wrapInsightProseHtml(formatAssistantInsightHtml(text, { translation }));

}



/**

 * BfVerseInsight — разбор стиха в том же визуальном стиле, что и BfAIInterpretation.

 */

export default function BfVerseInsight({

  bookId,

  chapter,

  verse,

  reference,

  verseText = '',

  translation = 'synodal',

  initialTab = 'explain',

  onClose,

}) {

  const navigate = useNavigate();

  const scrollRef = useRef(null);

  const scrollTopByTabRef = useRef({});

  const threadEndRefs = useRef({});

  const inputRef = useRef(null);

  const autoloadedRef = useRef(new Set());

  const [activeTab, setActiveTab] = useState(resolveInitialTab(initialTab));

  const [verseExpanded, setVerseExpanded] = useState(false);

  const [questionDraft, setQuestionDraft] = useState('');

  const [tgMobileFull, setTgMobileFull] = useState(isTgMobileFullscreen);



  const {

    messages,

    loading,

    tabErrors,

    ensureTabLoaded,

    submitQuestion,

    resetSession,

    retryTab,

  } = useVerseInsightChat({ bookId, chapter, verse, translation });



  useTelegramOverlayBack('verseInsight', true, onClose, TG_OVERLAY_Z.verseInsight);



  useEffect(() => {

    const syncLayout = () => setTgMobileFull(isTgMobileFullscreen());

    syncLayout();

    const root = document.documentElement;

    const mo = new MutationObserver(syncLayout);

    mo.observe(root, {

      attributes: true,

      attributeFilter: ['data-tg-desktop', 'data-tg-webapp', 'data-tg-fullscreen'],

    });

    window.addEventListener('resize', syncLayout);

    return () => {

      mo.disconnect();

      window.removeEventListener('resize', syncLayout);

    };

  }, []);



  useEffect(() => {

    resetSession();

    scrollTopByTabRef.current = {};

    autoloadedRef.current.clear();

    setVerseExpanded(false);

    setActiveTab(resolveInitialTab(initialTab));

    if (scrollRef.current) scrollRef.current.scrollTop = 0;

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [bookId, chapter, verse, translation]);



  useEffect(() => {

    const key = `${bookId}:${chapter}:${verse}:${activeTab}`;

    if (autoloadedRef.current.has(key)) return;

    autoloadedRef.current.add(key);

    void ensureTabLoaded(activeTab);

  }, [activeTab, bookId, chapter, verse, ensureTabLoaded]);



  const selectTab = (tabId) => {

    if (tabId === activeTab) return;

    if (scrollRef.current) {

      scrollTopByTabRef.current[activeTab] = scrollRef.current.scrollTop;

    }

    setActiveTab(tabId);

    requestAnimationFrame(() => {

      const thread = getQuestionThreadMessages(messages, tabId);

      const pendingOnTab = loading && messages[messages.length - 1]?.insightTab === tabId;

      if (thread.length > 0 || pendingOnTab) {

        threadEndRefs.current[tabId]?.scrollIntoView({ block: 'end', behavior: 'auto' });

        return;

      }

      const el = scrollRef.current;

      if (!el) return;

      const saved = scrollTopByTabRef.current[tabId];

      el.scrollTop = typeof saved === 'number' ? saved : 0;

    });

  };



  const scrollActiveThreadToBottom = useCallback((behavior = 'smooth') => {

    requestAnimationFrame(() => {

      threadEndRefs.current[activeTab]?.scrollIntoView({ block: 'end', behavior });

    });

  }, [activeTab]);



  const activeThread = getQuestionThreadMessages(messages, activeTab);

  const activeThreadKey = activeThread

    .map((m) => `${m.role}:${String(m.text || '').length}:${m.streaming ? 1 : 0}`)

    .join('|');

  const pendingQuestionOnActiveTab =

    loading && messages[messages.length - 1]?.insightTab === activeTab;

  useLayoutEffect(() => {

    if (!activeThreadKey && !pendingQuestionOnActiveTab) return;

    scrollActiveThreadToBottom(loading ? 'auto' : 'smooth');

  }, [activeThreadKey, pendingQuestionOnActiveTab, loading, scrollActiveThreadToBottom]);



  const handleInsightLinkClick = (e) => {

    const link = e.target.closest?.('a.bf-insight-ref, a.bf-chat-refs__item--link');

    if (!link) return;

    const href = link.getAttribute('href');

    if (!href || !href.startsWith('/')) return;

    e.preventDefault();

    e.stopPropagation();

    onClose?.();

    navigate(href);

  };



  const handleQuestionSubmit = (e) => {

    e?.preventDefault?.();

    const text = questionDraft.trim();

    if (!text || loading) return;

    setQuestionDraft('');

    void submitQuestion(text, activeTab);

  };



  const snippet = clipVerseSnippet(verseText);

  const displayVerse = verseExpanded ? verseText : snippet;

  const sheetClassName = [
    'bf-sheet--insight',
    tgMobileFull ? 'bf-sheet--ai-tg' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const backdropClassName = tgMobileFull ? 'bf-sheet-backdrop--ai-tg' : '';

  const bodyClassName = tgMobileFull ? 'bf-sheet__body--ai-full' : 'bf-sheet__body--ai-sheet';

  const renderChipTabContent = (tab) => {

    const tabError = tabErrors[tab];

    const assistant = getInsightSurfaceAssistant(messages, tab);

    const raw = String(assistant?.text || '');

    const hasText = raw.trim().length > 0;

    const busy = Boolean(assistant?.streaming) || (loading && !hasText);



    if (tabError && !hasText) {

      return (

        <div className="bf-error bf-error--inline">

          <p className="bf-error__body">{tabError}</p>

          <button type="button" className="bf-pill-cta" onClick={() => retryTab(tab)}>

            {t('retry')}

          </button>

        </div>

      );

    }



    if (!assistant && loading) {

      return (

        <div className="bf-loading bf-loading--inline">

          <div className="bf-ai-dot" />

          <div>{t('chatLoadingAnswer')}</div>

        </div>

      );

    }



    if (!hasText && busy) {

      return (

        <div className="bf-loading bf-loading--inline">

          <div className="bf-ai-dot" />

          <div>{t('chatLoadingAnswer')}</div>

        </div>

      );

    }



    if (!hasText) return null;



    return (

      <div

        className="bf-ai-pane bf-insight-prose-wrap"

        dangerouslySetInnerHTML={{ __html: formatInsightHtml(raw, translation) }}

        onClick={handleInsightLinkClick}

      />

    );

  };



  const renderQuestionThread = (tabId) => {
    const thread = getQuestionThreadMessages(messages, tabId);
    const questionErrorKey = `question_${tabId}`;
    const tabQuestionError = tabErrors[questionErrorKey];
    const lastMessage = messages[messages.length - 1];
    const showThread =
      thread.length > 0 ||
      (loading &&
        lastMessage?.insightTab === tabId &&
        lastMessage?.modeTag === ChatStreamMode.Question);

    if (!showThread) return null;

    return (
      <section className="bf-insight-question-thread" aria-label={t('verseInsightAskQuestion')}>
        {thread.map((m, idx) => {

          if (m.role === 'user') {

            return (

              <div key={`q-${idx}`} className="bf-insight-question-thread__user">

                {m.text}

              </div>

            );

          }

          if (m.role === 'assistant') {

            const raw = String(m.text || '');

            if (!raw.trim() && m.streaming) {

              return (

                <div key={`a-${idx}`} className="bf-loading bf-loading--inline">

                  <div className="bf-ai-dot" />

                  <div>{t('chatLoadingAnswer')}</div>

                </div>

              );

            }

            if (!raw.trim()) return null;

            return (

              <div

                key={`a-${idx}`}

                className={`bf-insight-question-thread__assistant${m.isError ? ' bf-insight-question-thread__assistant--error' : ''}`}

                dangerouslySetInnerHTML={{ __html: formatInsightHtml(raw, translation) }}

                onClick={handleInsightLinkClick}

              />

            );

          }

          return null;

        })}

        {tabQuestionError && !loading && (
          <div className="bf-error bf-error--inline">
            <p className="bf-error__body">{tabQuestionError}</p>
          </div>
        )}
        <div
          ref={(el) => {
            if (el) threadEndRefs.current[tabId] = el;
            else delete threadEndRefs.current[tabId];
          }}
          className="bf-insight-thread-anchor"
          aria-hidden="true"
        />
      </section>
    );
  };



  const questionFooter = (

    <form className="bf-insight-composer" onSubmit={handleQuestionSubmit}>

      <input

        ref={inputRef}

        type="text"

        className="bf-insight-composer__input"

        value={questionDraft}

        maxLength={300}

        disabled={loading}

        placeholder={t('chatComposerPlaceholderVerse')}

        aria-label={t('verseInsightAskQuestion')}

        onChange={(e) => setQuestionDraft(e.target.value)}

      />

      <button

        type="submit"

        className="bf-insight-composer__send"

        disabled={loading || !questionDraft.trim()}

        aria-label={t('chat')}

      >

        →

      </button>

    </form>

  );



  return (

    <BfSheet

      sheetClassName={sheetClassName}

      backdropClassName={backdropClassName}

      bodyClassName={bodyClassName}

      onClose={onClose}

      showHeaderClose={false}

      ariaLabel={t('verseInsightDialogAria')}

      footer={questionFooter}

    >

      <div className="bf-ai-layout">

        <div className="bf-ai-layout__head">

          <div className="bf-insight-verse-head">

            <button

              type="button"

              className={`bf-insight-verse-head__toggle${verseExpanded ? ' is-expanded' : ''}`}

              onClick={() => setVerseExpanded((v) => !v)}

              aria-expanded={verseExpanded}

              aria-label={

                verseExpanded ? t('verseInsightVerseCollapseAria') : t('verseInsightVerseExpandAria')

              }

            >

              <span className="bf-insight-verse-head__ref">{reference}</span>

              {displayVerse ? (

                <span className="bf-insight-verse-head__text">«{displayVerse}»</span>

              ) : null}

            </button>

            {onClose ? (

              <button

                type="button"

                className="bf-icon-btn bf-insight-verse-head__close"

                onClick={onClose}

                aria-label={t('close')}

              >

                <BfIconClose />

              </button>

            ) : null}

          </div>



          <div

            className="bf-ai-tabs"

            role="tablist"

            aria-label={t('verseInsightDialogAria')}

          >

            {INSIGHT_TABS.map((tab) => (

              <button

                key={tab}

                id={`bf-insight-tab-${tab}`}

                type="button"

                role="tab"

                aria-selected={activeTab === tab}

                aria-controls={`bf-insight-panel-${tab}`}

                className="bf-chip bf-ai-tabs__chip"

                data-active={activeTab === tab ? 'true' : 'false'}

                onClick={() => selectTab(tab)}

              >

                {tabLabel(tab)}

              </button>

            ))}

          </div>

        </div>



        <div className="bf-ai-layout__scroll" ref={scrollRef} onClick={handleInsightLinkClick}>

          {INSIGHT_TABS.map((tab) => (

            <div

              key={tab}

              id={`bf-insight-panel-${tab}`}

              className="bf-ai-tab-panel"

              role="tabpanel"

              aria-labelledby={`bf-insight-tab-${tab}`}

              hidden={activeTab !== tab}

            >

              {renderChipTabContent(tab)}

              {renderQuestionThread(tab)}

            </div>

          ))}

        </div>

      </div>

    </BfSheet>

  );

}


